// src/hooks/useNotifyInbox.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense Notify — inbox state for the notification centre
//
//   const inbox = useNotifyInbox({ view: 'unread', category: 'sla', q: '', enabled: isOpen });
//   inbox.items / inbox.loading / inbox.error / inbox.hasMore / inbox.loadMore()
//   inbox.markRead(id) · markUnread · acknowledge · archive · unarchive · snooze(id, min) · remove · readAll()
//
// • cursor pagination (30 per page), filters + debounced search
// • optimistic actions with rollback (the promise rejects so the caller can toast)
// • live: new notifications are merged in (deduped by id); changes made on another device
//   ('notification_update') are applied locally
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../services/notifyApi';
import notifyStream from '../services/notifyStream';

const PAGE = 30;

function matchesView(n, view, category) {
    if (category && n.category !== category) return false;
    if (view === 'archived') return Boolean(n.archived_at);
    if (n.archived_at) return false;
    if (view === 'snoozed') return Boolean(n.snoozed_until && new Date(n.snoozed_until) > new Date());
    if (n.snoozed_until && new Date(n.snoozed_until) > new Date()) return false;
    if (view === 'unread') return !n.is_read;
    if (view === 'ack') return n.requires_ack && !n.acknowledged_at;
    if (view === 'critical') return n.priority === 'critical' || n.priority === 'high';
    return true;
}

/** Local effect of an action (shared by optimistic updates and live sync). */
function applyChange(items, { action, ids = [], category }, view, catFilter) {
    const set = new Set((ids || []).map(String));
    const now = new Date().toISOString();
    let next = items.map((n) => {
        if (action === 'read_all' ? (!category || n.category === category) && !n.archived_at : set.has(String(n.id))) {
            switch (action) {
                case 'read': case 'read_all': return { ...n, is_read: true, read_at: n.read_at || now };
                case 'unread': return { ...n, is_read: false, read_at: null };
                case 'acknowledged': return { ...n, acknowledged_at: n.acknowledged_at || now, is_read: true, read_at: n.read_at || now };
                case 'archived': return { ...n, archived_at: now, is_read: true };
                case 'unarchived': return { ...n, archived_at: null };
                case 'snoozed': return { ...n, snoozed_until: new Date(Date.now() + 3600000).toISOString() };
                case 'escalated': return { ...n, escalated_at: now, escalation_level: (n.escalation_level || 0) + 1 };
                case 'deleted': return null;
                default: return n;
            }
        }
        return n;
    }).filter(Boolean);
    // Drop rows that no longer belong in this view (keep read rows in "unread" until refresh — less jumpy)
    if (['archived', 'unarchived', 'snoozed', 'acknowledged'].includes(action)) {
        next = next.filter(n => matchesView(n, view, catFilter) || (view === 'unread' && n.is_read && !n.archived_at));
    }
    return next;
}

export default function useNotifyInbox({ view = 'all', category = '', q = '', enabled = true } = {}) {
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [debouncedQ, setDebouncedQ] = useState(q);
    const isMounted = useRef(true);
    const abortRef = useRef(null);
    const itemsRef = useRef(items);
    itemsRef.current = items;
    const viewRef = useRef({ view, category });
    viewRef.current = { view, category };

    useEffect(() => () => { isMounted.current = false; abortRef.current?.abort(); }, []);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(q), 300);
        return () => clearTimeout(t);
    }, [q]);

    const load = useCallback(async () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        setLoading(true);
        setError(null);
        try {
            const page = await api.getInbox({ view, category: category || undefined, q: debouncedQ || undefined, limit: PAGE }, ctrl.signal);
            if (!isMounted.current || ctrl.signal.aborted) return;
            setItems(page.items);
            setCursor(page.nextCursor);
        } catch (e) {
            if (e?.name === 'AbortError' || !isMounted.current) return;
            setError(e);
        } finally {
            if (isMounted.current && abortRef.current === ctrl) setLoading(false);
        }
    }, [view, category, debouncedQ]);

    useEffect(() => { if (enabled) load(); }, [enabled, load]);

    const loadMore = useCallback(async () => {
        if (!cursor || loadingMore) return;
        setLoadingMore(true);
        try {
            const page = await api.getInbox({ view, category: category || undefined, q: debouncedQ || undefined, limit: PAGE, cursor });
            if (!isMounted.current) return;
            setItems(prev => {
                const seen = new Set(prev.map(n => n.id));
                return [...prev, ...page.items.filter(n => !seen.has(n.id))];
            });
            setCursor(page.nextCursor);
        } catch (e) {
            if (isMounted.current) setError(e);
        } finally {
            if (isMounted.current) setLoadingMore(false);
        }
    }, [cursor, loadingMore, view, category, debouncedQ]);

    // Live merge + cross-device sync
    useEffect(() => {
        if (!enabled) return undefined;
        const offNew = notifyStream.on('notification', (payload) => {
            const n = payload?.notification;
            if (!n || debouncedQ) return; // a search result list is not live-merged
            const { view: v, category: c } = viewRef.current;
            if (!matchesView(n, v, c)) return;
            setItems(prev => (prev.some(x => x.id === n.id) ? prev : [n, ...prev]));
        });
        const offUpd = notifyStream.on('notification_update', (change) => {
            if (!change?.action) return;
            const { view: v, category: c } = viewRef.current;
            setItems(prev => applyChange(prev, change, v, c));
        });
        const offPoll = notifyStream.on('poll', () => load());
        return () => { offNew(); offUpd(); offPoll(); };
    }, [enabled, debouncedQ, load]);

    /** Optimistic helper: apply locally, call the API, roll back on failure. */
    const optimistic = useCallback(async (change, call) => {
        const snapshot = itemsRef.current;
        const { view: v, category: c } = viewRef.current;
        setItems(prev => applyChange(prev, change, v, c));
        try {
            return await call();
        } catch (e) {
            if (isMounted.current) setItems(snapshot);
            throw e;
        }
    }, []);

    const actions = useMemo(() => ({
        markRead: (id) => optimistic({ action: 'read', ids: [id] }, () => api.markRead([id])),
        markUnread: (id) => optimistic({ action: 'unread', ids: [id] }, () => api.markUnread([id])),
        acknowledge: (id) => optimistic({ action: 'acknowledged', ids: [id] }, () => api.acknowledge(id)),
        archive: (id) => optimistic({ action: 'archived', ids: [id] }, () => api.archive([id])),
        unarchive: (id) => optimistic({ action: 'unarchived', ids: [id] }, () => api.unarchive([id])),
        snooze: (id, minutes) => optimistic({ action: 'snoozed', ids: [id] }, () => api.snooze(id, minutes)),
        remove: (id) => optimistic({ action: 'deleted', ids: [id] }, () => api.removeNotifications([id])),
        readAll: (cat) => optimistic({ action: 'read_all', category: cat || null }, () => api.readAll(cat || undefined)),
    }), [optimistic]);

    return {
        items,
        loading,
        loadingMore,
        error,
        hasMore: Boolean(cursor),
        reload: load,
        loadMore,
        ...actions,
    };
}

export { matchesView, applyChange };
